import { getSlug } from '@lib/utils';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';

import Modal from '@components/modal';
import NotificationCard from '@components/notification-card';
import NotesCardModal from '@components/notes/notes-card-modal';
import ShareModal from '@components/modal/share-modal';
import RequestATourModal from '@components/request-a-tour-modal/component';
import OpenHouseModal from '@components/open-house-modal';
import ContactForm from '@components/forms';
import { useListingContext } from '../../context';

import {
  notification_modal,
  notification_header,
  notification_card,
  browser_fix,
  notes_modal
} from './styles.module.scss';

const PropertyModals = ({
  shareModalIsOpen,
  setShareModalIsOpen,
  preApprovalModalIsOpen,
  setPreApprovalModalIsOpen,
  notesModalIsOpen,
  setNotesModalIsOpen,
  notificationModalIsOpen,
  setNotificationModalIsOpen,
  requestATourModalIsOpen,
  setRequestATourModalIsOpen,
  openHouseModalIsOpen,
  setOpenHouseModalIsOpen,
  updateNoteListings
}) => {
  const router = useRouter();
  const { userInfo } = useSelector((state) => state.user);
  const { saved, currentListing } = useSelector((state) => state.user);

  const {
    openHouses,
    listingId: id,
    primaryDisplayAgent,
    associatedAgent,
    informationRequestDefaultComments,
    address,
    city,
    state,
    zip,
    photos,
    canonicalUrl,
    hasMapLocationExternalLink,
    mapLocationExternalLink,
    sharingServicesCollection,
    priceFormatted
  } = useListingContext();

  const savedListing =
    saved && saved.length > 0 ? saved.find(({ listingId }) => listingId === id) : null;

  const hasOpenHouse = openHouses[0];

  const selectedNotifications = [
    'shouldNotifyOnPriceChange',
    'shouldNotifyOnStatusChange',
    'shouldNotifyOnOpenHouseScheduled'
  ].filter((el) => currentListing?.[el]);

  const onOpenHouseModalClose = () => {
    setOpenHouseModalIsOpen(false);
  };

  const onNotificationModalClose = () => {
    setNotificationModalIsOpen(false);
  };

  const onNotesModalClose = () => {
    setNotesModalIsOpen(false);
  };

  const notificationModalContent = (
    <div className={notification_modal}>
      <div className={notification_header}>
        <div className={browser_fix}>Set up N</div>
        <div className={browser_fix}>otifications!</div>
        <div>Don't miss out on this property</div>
      </div>
      <div className={notification_card}>
        <NotificationCard
          savedListingId={savedListing?.savedListingId}
          button={{
            label: 'Update Notifications'
          }}
          selectedNotifications={selectedNotifications || []}
          checkboxes={{
            items: [
              {
                key: 'priceChange',
                label: 'Price change',
                value: 'shouldNotifyOnPriceChange'
              },
              {
                key: 'statusChange',
                label: 'Status change',
                value: 'shouldNotifyOnStatusChange'
              },
              {
                key: 'openHouse',
                label: 'An open house scheduled',
                value: 'shouldNotifyOnOpenHouseScheduled'
              }
            ],
            selectAllLabel: null
          }}
          propertyNotificationModalIsOpen={notificationModalIsOpen}
          setPropertyNotificationModalIsOpen={setNotificationModalIsOpen}
          heading="Notify me when this listing has:"
          image={photos[0]?.midSizeImageUrl}
          property={{
            address: address,
            city: city,
            listingId: id,
            priceFormatted: String(priceFormatted || ''),
            state: state,
            zip: zip
          }}
          listingId={id}
          listingUrl={`/listing/${id}`}
          url={canonicalUrl ? getSlug(canonicalUrl) : `/listing/${id}`}
        />
      </div>
    </div>
  );

  const preApprovalModalContent = (
    <div>
      <h2>Let's Move!</h2>
      <p>
        One of our loan officers will be in touch with you shortly to accelerate your pre-approval.
      </p>
      <ContactForm
        agentId={primaryDisplayAgent?.agentId || associatedAgent?.agentId || 0}
        theme="no-background-no-padding"
        linkLabel="Submit"
        endpoint="get pre-approved"
        endpointId={id}
        defaultFormValue={`I would like to be pre-approved for the property at ${address}. Please contact me with next steps.`}
        userInfo={userInfo}
      />
    </div>
  );

  const notesModalContent = (
    <div className={notes_modal}>
      <NotesCardModal
        image={photos[0]?.midSizeImageUrl}
        listingId={id}
        url={canonicalUrl ? getSlug(canonicalUrl) : `/listing/${id}`}
        listingUrl={`/listing/${id}`}
        property={{
          address,
          city,
          priceFormatted: String(priceFormatted || ''),
          state,
          zip
        }}
        updateNoteListings={updateNoteListings}
        modalIsOpen={notesModalIsOpen}
        setModalIsOpen={setNotesModalIsOpen}
        updateParent={false}
      />
    </div>
  );

  return (
    <>
      {shareModalIsOpen && (
        <ShareModal
          modalIsOpen={shareModalIsOpen}
          onModalClose={() => setShareModalIsOpen(false)}
          sharingServicesCollection={sharingServicesCollection}
          copyUrl={canonicalUrl}
        />
      )}

      {preApprovalModalIsOpen && (
        <Modal
          modalIsOpen={preApprovalModalIsOpen}
          onModalClose={() => setPreApprovalModalIsOpen(false)}
          content={preApprovalModalContent}
          theme="pre-approval"
        />
      )}

      {notesModalIsOpen && (
        <Modal
          modalIsOpen={notesModalIsOpen}
          onModalClose={onNotesModalClose}
          content={notesModalContent}
          theme="notes"
        />
      )}

      {notificationModalIsOpen && (
        <Modal
          modalIsOpen={notificationModalIsOpen}
          onModalClose={onNotificationModalClose}
          content={notificationModalContent}
        />
      )}

      {requestATourModalIsOpen && (
        <RequestATourModal
          listingId={id}
          agentId={primaryDisplayAgent?.agentId || associatedAgent?.agentId || 0}
          modalIsOpen={requestATourModalIsOpen}
          setModalIsOpen={setRequestATourModalIsOpen}
          defaultFormValue={informationRequestDefaultComments}
          userInfo={userInfo}
          address={address}
          city={city}
          state={state}
          zip={zip}
          photos={photos}
          listingUrl={canonicalUrl ? getSlug(canonicalUrl) : `/listing/${id}`}
        />
      )}

      {openHouseModalIsOpen && hasOpenHouse && (
        <OpenHouseModal
          modalIsOpen={openHouseModalIsOpen}
          setModalIsOpen={onOpenHouseModalClose}
          agentId={
            router?.query?.openhouse ||
            primaryDisplayAgent?.agentId ||
            router?.query?.agentid ||
            associatedAgent?.agentId ||
            0
          }
          listingId={id}
          userInfo={userInfo}
          address={address}
          city={city}
          state={state}
          zip={zip}
          photos={photos}
          listingUrl={canonicalUrl ? getSlug(canonicalUrl) : `/listing/${id}`}
          openHouses={openHouses}
          directionsUrl={hasMapLocationExternalLink ? mapLocationExternalLink : null}
        />
      )}
    </>
  );
};

export default PropertyModals;
